import { useCallback, useEffect, useMemo } from 'react';

import { atom, useAtomValue, useSetAtom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { ALL_FORMATS, BlobSource, Input, InputVideoTrack } from 'mediabunny';

import { withRetry } from '@core';

export const VIDEO_STATUS = {
    INIT: 'INIT',
    LOADING: 'LOADING',
    ERROR: 'ERROR',
    READY: 'READY',
} as const;

const VIDEO_LOAD_RETRIES = 5;
const VIDEO_LOAD_RETRY_DELAY = 500;

const initialState = { status: VIDEO_STATUS.INIT } as const;

export type VideoMetadata = {
    duration: number;
    decoderConfig: VideoDecoderConfig;
};

type LoadState =
    | { status: typeof VIDEO_STATUS.INIT }
    | { status: typeof VIDEO_STATUS.LOADING; controller: AbortController }
    | {
          status: typeof VIDEO_STATUS.READY;
          input: Input<BlobSource>;
          track: InputVideoTrack;
          metadata: VideoMetadata;
      }
    | { status: typeof VIDEO_STATUS.ERROR; error: unknown };

type Cache = Record<string, LoadState>;
type CacheMutation = (loadState: LoadState) => LoadState;

export const cacheAtom = atom<Cache>({});

const isAbortError = (error: unknown) => error instanceof DOMException && error.name === 'AbortError';

const loadEncodedVideoBlob = async (src: string, signal: AbortSignal) => {
    const request = await fetch(src, { signal });

    if (!request.ok) {
        throw new Error(`Failed to load video: ${request.status} ${request.statusText}`);
    }

    const blob = await request.blob();

    return new Input({
        formats: ALL_FORMATS,
        source: new BlobSource(blob),
    });
};

const loadVideoTrack = async (input: Input<BlobSource>) => {
    const track = await input.getPrimaryVideoTrack();

    if (!track) {
        throw new Error('No video track');
    }

    const [duration, decoderConfig] = await Promise.all([track.computeDuration(), track.getDecoderConfig()]);

    if (!decoderConfig) {
        throw new Error('No video decoder config');
    }

    return {
        input,
        track,
        metadata: {
            duration,
            decoderConfig,
        },
    };
};

const useVideoCache = (src: string) => {
    const stateAtom = useMemo(() => selectAtom(cacheAtom, (cache) => cache[src] ?? initialState), [src]);
    const state = useAtomValue(stateAtom);
    const updateCache = useSetAtom(cacheAtom);

    const updateState = useCallback(
        (updatePayload: CacheMutation | LoadState) =>
            updateCache((cache) => {
                const loadState = cache[src] ?? initialState;
                const nextState = typeof updatePayload === 'function' ? updatePayload(loadState) : updatePayload;

                return {
                    ...cache,
                    [src]: nextState,
                };
            }),
        [src, updateCache],
    );

    useEffect(() => {
        return () => {
            updateState((loadState) => {
                if (loadState.status === VIDEO_STATUS.LOADING) {
                    loadState.controller.abort();

                    return { status: VIDEO_STATUS.INIT };
                }

                if (loadState.status === VIDEO_STATUS.ERROR) {
                    return { status: VIDEO_STATUS.INIT };
                }

                return loadState;
            });
        };
    }, [updateState]);

    return [state, updateState] as const;
};

export const useVideo = (src: string) => {
    const [state, updateState] = useVideoCache(src);

    const loadVideo = useCallback(
        (controller: AbortController) =>
            loadEncodedVideoBlob(src, controller.signal)
                .then(loadVideoTrack)
                .then(({ input, track, metadata }) => {
                    updateState((loadState) => {
                        if (
                            loadState.status !== VIDEO_STATUS.LOADING ||
                            loadState.controller !== controller ||
                            controller.signal.aborted
                        ) {
                            return loadState;
                        }

                        return {
                            status: VIDEO_STATUS.READY,
                            input,
                            track,
                            metadata,
                        };
                    });
                }),
        [src, updateState],
    );

    const showError = useCallback(
        (error: unknown, controller: AbortController) => {
            if (controller.signal.aborted || isAbortError(error)) {
                return;
            }

            updateState((loadState) => {
                if (
                    loadState.status !== VIDEO_STATUS.LOADING ||
                    loadState.controller !== controller ||
                    controller.signal.aborted
                ) {
                    return loadState;
                }

                return {
                    status: VIDEO_STATUS.ERROR,
                    error,
                };
            });
        },
        [updateState],
    );

    const loadVideoWithRetry = useCallback(
        (controller: AbortController) =>
            withRetry(() => loadVideo(controller), VIDEO_LOAD_RETRIES, VIDEO_LOAD_RETRY_DELAY, controller.signal).catch(
                (error) => showError(error, controller),
            ),
        [loadVideo, showError],
    );

    useEffect(() => {
        if (state.status !== VIDEO_STATUS.INIT) {
            return;
        }

        const controller = new AbortController();

        updateState((loadState) => {
            if (loadState.status !== VIDEO_STATUS.INIT) {
                controller.abort();

                return loadState;
            }

            loadVideoWithRetry(controller);

            return {
                status: VIDEO_STATUS.LOADING,
                controller,
            };
        });
    }, [state.status, updateState, loadVideoWithRetry]);

    return state;
};
